// Kept out of the RequestListing barrel: the notification listener shows it on
// every page, so it must not pull the request list into the main bundle.
import {ReactNode} from "react";
import {Link, ListItem, ModalProps, Text, UnorderedList} from "@chakra-ui/react";
import {PopupInfo} from "../../models";
import {ACCESS_LINK, ACCOUNT_VERIFY_IDENTITY} from "../../helper/constants";
import {ConfirmationModal, Description} from "../UI/CustomModal";
import {L5Button} from "../UI/CustomButton";

export type Role =
|"identity-verified"
|"biometric"
|"participant"
|"resolver"
|"verification"
|"verification-pending";
type OnboardingProps = Omit<ModalProps, "children"> & {
    role: Role
    children?: ReactNode
};

export const infos: Record<Role, PopupInfo> = {
    biometric: {
        desc: "To complete this action, we need to verify your identity using" +
        " biometrics (fingerprint or face ID). This ensures that only you can" +
        " authorize important decisions, keeping  your requests protected.",
        node: <Links/>,
        title: "Secure Your Account with Biometrics"
    },
    "identity-verified": {
        desc: "Great news, your identity has been successfully verified! You " +
        "can now proceed with your request.",
        node: <IdentificationNote/>,
        title: "Verification complete"
    },
    participant: {
        desc: "You were included for context on a request sent through Admiral"+
        " Resolve. Resolve is used by logistics teams to reduce impersonation" +
        " and strategic theft by requiring requests to be sent by a verified " +
        "sender, at the moment they’re created. ",
        icon: "warehouse",
        iconFill: "#017172",
        node: <ParticipantNote/>,
        title: "You have been added to a request"
    },
    resolver: {
        desc: "You have been asked to resolve a request sent through Admiral" +
            " Resolve. Resolve is used by logistics teams to reduce impersonation" +
            " and strategic theft by requiring requests to be sent by a verified " +
            " sender, at the moment they’re created.",
        icon: "warehouse",
        iconFill: "#017172",
        node: <ResolveDisclaimer/>,
        title: "Review and resolve requests"
    },
    verification: {
        action: {
            label: "Verify my identity",
            link: ACCOUNT_VERIFY_IDENTITY
        },
        desc: "To protect your account and ensure your intent is processed " +
            "securely, we need to confirm your identity. This quick check helps " +
            "prevent unauthorized access and keeps your information safe.",
        title: "Please Verify Your Identity"
    },
    "verification-pending": {
        action: {
            label: "Check my verification",
            link: ACCOUNT_VERIFY_IDENTITY
        },
        desc: "We’re currently verifying your identity. This usually takes " +
        "just a few moments—please hold on.",
        node: <VerificationPending/>,
        title: "Verification in progress"
    }
};

function ResolveDisclaimer() {
    return <Text textAlign="center" style={{textWrap: "pretty"}}>
        Resolve does not decide whether the request should be acted on.
        That decision is always yours.
    </Text>;
}

function VerificationPending() {
    return <>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            You can continue with your request as soon as it’s approved; this
            page notices it by itself. To see where it stands, check your
            verification in your account.
        </Text>
    </>;
}

function IdentificationNote() {
    return <>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            We’ve also sent you a confirmation email for your records. If you
            need any further assistance, don’t hesitate to reach out.
        </Text>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            Thank you for your patience!
        </Text>
    </>;
}
function ParticipantNote() {
    return <Text textAlign="center" style={{textWrap: "pretty"}}>
        You are not responsible for resolving this request.
    </Text>;
}

function Links() {
    return <div className="stack">
        <Text fontWeight="bold" textAlign="center">Set up biometrics now :</Text>
        <UnorderedList spacing={3} px=".25rem">
            <ListItem><Link href={ACCESS_LINK.google} target="_blank">by downloading Admiral Access For Android</Link></ListItem>
            <ListItem><Link href={ACCESS_LINK.apple} target="_blank">by downloading Admiral Access For IOS</Link></ListItem>
        </UnorderedList>
    </div>;
}

function OnboardingModal(p: OnboardingProps) {
    const info = infos[p.role];
    const proceedingRoles: Role[] = [
        "resolver",
        "participant",
        "identity-verified",
        "verification-pending"
    ];
    return <ConfirmationModal
        {...p}
        description={<Description
            description={info.desc}
            title={info.title}
        >
            {info.node}
        </Description>}
        icon={info.icon}
        iconColor={info.iconFill}
    >
        {info.action && <Link
            bg="primary.500"
            color="white"
            px="1rem"
            py=".5rem"
            transition="all .35s ease"
            href={info.action.link}
            borderRadius=".5rem"
            fontWeight="medium"
            textAlign="center"
        >
            {info.action.label}
        </Link>}
        {proceedingRoles.includes(p.role) && <L5Button
            type="primary"
            onClick={p.onClose}
        >
            Continue
        </L5Button>}
    </ConfirmationModal>
}

export {OnboardingModal};
